import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, Tab, IconButton, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SyncedTabs from './SyncedTabs';
import JsonSyntaxHighlight from './JsonSyntaxHighlight';
import SearchBar from './SearchBar';
import { prettyPrintJsonSync, isHugeResponse, isMassiveResponse, looksLikeJson, formatHtmlForDisplay, formatXml } from '../utils/responseBodyUtils';

/** Same approach as Collections response sub-tabs: MUI ink misaligns under zoom; hide it and draw underline on the selected tab. */
const LAZY_RESPONSE_BODY_TAB_INDICATOR = { sx: { display: 'none', height: 0, visibility: 'hidden' } };

const MAX_SEARCH_MATCHES = 500;

function findMatchRanges(
  text: string,
  query: string,
  caseInsensitive: boolean,
  useRegex: boolean
): { start: number; end: number }[] {
  if (!query.trim() || !text) return [];
  const ranges: { start: number; end: number }[] = [];
  try {
    if (useRegex) {
      const re = new RegExp(query, caseInsensitive ? 'gi' : 'g');
      let match: RegExpExecArray | null;
      while ((match = re.exec(text)) !== null) {
        const len = match[0].length;
        ranges.push({ start: match.index, end: match.index + (len || 1) });
        if (len === 0) re.lastIndex += 1;
        if (ranges.length >= MAX_SEARCH_MATCHES) break;
      }
    } else {
      const haystack = caseInsensitive ? text.toLowerCase() : text;
      const needle = caseInsensitive ? query.toLowerCase() : query;
      let pos = 0;
      while ((pos = haystack.indexOf(needle, pos)) >= 0) {
        ranges.push({ start: pos, end: pos + needle.length });
        if (ranges.length >= MAX_SEARCH_MATCHES) break;
        pos += 1;
      }
    }
  } catch {
    return [];
  }
  return ranges;
}

function lazyResponseBodyTabsSx(theme: Theme) {
  return {
    minHeight: 36,
    minWidth: 0,
    width: '100%',
    bgcolor: 'transparent',
    '& .MuiTabs-indicator': {
      display: 'none !important',
      height: 0,
      visibility: 'hidden',
    },
    '& .MuiTabs-flexContainer': { gap: 0 },
    '& .MuiTab-root': {
      position: 'relative',
      overflow: 'visible',
      minHeight: 36,
      minWidth: 0,
      flex: '0 0 auto',
      textTransform: 'none',
      py: 0.5,
      '&.Mui-selected': {
        fontWeight: 600,
      },
      '&.Mui-selected::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 3,
        borderRadius: '3px 3px 0 0',
        backgroundColor: theme.palette.primary.main,
      },
    },
  };
}

interface LazyResponseBodyProps {
  rawBody: string;
  headers?: Record<string, string>;
  height?: string | number;
  /** When provided, Search bar is rendered in the Pretty/Raw tab row instead of inside the Pretty toolbar */
  searchContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Tooltip / aria for the copy control (default: response body). */
  copyLabel?: string;
  /** Empty-state label for the Headers tab (default: response headers). */
  headersEmptyMessage?: string;
  /** Tooltip when copying from the Headers tab (default: response headers). */
  headersCopyLabel?: string;
}

/**
 * Milestone 5: Lazy + incremental response rendering.
 * - Pretty is the first and default tab; Raw is the second (POST/PUT/GET/DELETE).
 * - Pretty is computed synchronously for non-huge bodies so it shows immediately with no loading delay.
 * - For huge responses, avoids expanding by default.
 *
 * Postman-style: We NEVER truncate or limit the response. Full rawBody (including large Base64
 * image data, 1000+ records, multi-MB payloads) is always displayed in full. No artificial row
 * limits, no stripping of large fields; only practical limit is system memory.
 */
const LazyResponseBody: React.FC<LazyResponseBodyProps> = ({
  rawBody,
  headers,
  height = '400px',
  searchContainerRef,
  copyLabel = 'Copy response body',
  headersEmptyMessage = 'No response headers',
  headersCopyLabel = 'Copy response headers',
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [hugePretty, setHugePretty] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [caseInsensitive, setCaseInsensitive] = useState(true);
  const [useRegex, setUseRegex] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [toolbarHost, setToolbarHost] = useState<HTMLDivElement | null>(null);
  const localSearchSlotRef = useRef<HTMLDivElement>(null);
  const searchSlotRef = searchContainerRef ?? localSearchSlotRef;
  const activeMatchRef = useRef<HTMLElement | null>(null);

  const headerEntries = headers ? Object.entries(headers) : [];
  const headersText = headerEntries.map(([key, value]) => `${key}: ${value ?? ''}`).join('\n');

  const handleCopy = async () => {
    const text = activeTab === 4 ? headersText : (rawBody || '');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const massive = isMassiveResponse(rawBody);
  const huge = isHugeResponse(rawBody);

  // Compute pretty view synchronously so it's ready on first render (no "Pretty (loading…)" delay).
  // Multi-MB bodies skip parse/pretty-print — that freezes Chromium (e.g. GitHub OpenAPI ~6.6MB).
  const displayPretty = useMemo(
    () => (rawBody && rawBody.trim() !== '' && !massive ? prettyPrintJsonSync(rawBody) : ''),
    [rawBody, massive]
  );

  // Auto-switch tab based on response Content-Type, with body fallback when Content-Type is missing (Postman-style)
  useEffect(() => {
    const contentType = headers
      ? (headers['content-type'] ?? headers['Content-Type'] ?? Object.entries(headers).find(([k]) => k.toLowerCase() === 'content-type')?.[1] ?? '')
      : '';
    const ct = (typeof contentType === 'string' ? contentType : '').toLowerCase().split(';')[0].trim();
    let tab: number;
    if (massive) {
      // Never Pretty: JSON.parse + pretty-print + tokenize of multi-MB JSON locks the page.
      if (ct.includes('text/html')) {
        tab = 2;
      } else if (ct.includes('application/xml') || ct.includes('text/xml') || ct.includes('+xml')) {
        tab = 3;
      } else {
        tab = 1; // Raw
      }
    } else if (ct.includes('text/html')) {
      tab = 2; // HTML
    } else if (ct.includes('application/xml') || ct.includes('text/xml') || ct.includes('+xml')) {
      tab = 3; // XML
    } else if (ct.includes('application/json') || ct.includes('+json')) {
      tab = 0; // Pretty JSON
    } else if (ct && !ct.includes('text/plain')) {
      tab = 1; // Raw for other known non-JSON/HTML/XML types
    } else {
      // No Content-Type or text/plain: infer from body so view still auto-switches
      const trimmed = (rawBody && typeof rawBody === 'string' ? rawBody : '').trim();
      if (trimmed.length === 0) {
        tab = 0;
      } else if (/^\s*<!DOCTYPE\s/i.test(trimmed) || /^\s*<html[\s>]/i.test(trimmed)) {
        tab = 2; // HTML
      } else if (/^\s*<\?xml\s/i.test(trimmed)) {
        tab = 3; // XML
      } else if (looksLikeJson(trimmed)) {
        tab = 0; // JSON-looking (do not JSON.parse — that blocks on large payloads)
      } else {
        tab = 1; // Raw for unsupported formats
      }
    }
    setActiveTab(tab);
  }, [rawBody, headers, massive]);

  // For huge-but-not-massive responses, pretty-print in the background so Pretty can format without blocking first paint.
  useEffect(() => {
    if (!huge || massive || !rawBody || rawBody.trim() === '') {
      setHugePretty(null);
      return;
    }
    setHugePretty(null);
    const id = setTimeout(() => {
      try {
        const parsed = JSON.parse(rawBody);
        setHugePretty(JSON.stringify(parsed, null, 2));
      } catch {
        setHugePretty(rawBody);
      }
    }, 0);
    return () => clearTimeout(id);
  }, [huge, massive, rawBody]);
  const displayRaw = rawBody || '(empty)';

  const displayHtml = useMemo(
    () => (rawBody && activeTab === 2 ? formatHtmlForDisplay(rawBody) : ''),
    [rawBody, activeTab]
  );
  const displayXml = useMemo(
    () => (rawBody && activeTab === 3 ? formatXml(rawBody) : ''),
    [rawBody, activeTab]
  );

  const virtualBody = (text: string) => (
    <JsonSyntaxHighlight
      json={text}
      maxHeight={height}
      copyContent={rawBody}
      copyLabel={copyLabel}
      searchContainerRef={searchSlotRef}
    />
  );

  const preSx = {
    p: 2,
    m: 0,
    overflow: 'visible',
    fontFamily: 'monospace',
    fontSize: 13,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word',
  };

  const syntaxOwnsToolbar =
    activeTab === 0 || ((activeTab === 1 || activeTab === 2 || activeTab === 3) && (huge || massive));
  const showPlainToolbar = !syntaxOwnsToolbar;

  const plainBodyText =
    activeTab === 1 ? displayRaw : activeTab === 2 ? (displayHtml || '(empty)') : activeTab === 3 ? (displayXml || '(empty)') : '';

  const bodyMatches = useMemo(
    () =>
      showPlainToolbar && activeTab >= 1 && activeTab <= 3
        ? findMatchRanges(plainBodyText, searchQuery, caseInsensitive, useRegex)
        : [],
    [showPlainToolbar, activeTab, plainBodyText, searchQuery, caseInsensitive, useRegex]
  );

  const headerSearch = useMemo(() => {
    const byCell = new Map<string, { start: number; end: number; flatIndex: number }[]>();
    let count = 0;
    if (activeTab !== 4 || !headers) return { byCell, count };
    Object.entries(headers).forEach(([key, value], row) => {
      ([['key', key], ['value', String(value ?? '')]] as const).forEach(([field, cell]) => {
        const ranges = findMatchRanges(cell, searchQuery, caseInsensitive, useRegex);
        if (ranges.length === 0) return;
        byCell.set(
          `${row}:${field}`,
          ranges.map((range) => ({ ...range, flatIndex: count++ }))
        );
      });
    });
    return { byCell, count };
  }, [activeTab, headers, searchQuery, caseInsensitive, useRegex]);

  const matchCount = activeTab === 4 ? headerSearch.count : bodyMatches.length;

  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchQuery, caseInsensitive, useRegex, activeTab]);

  useEffect(() => {
    setCurrentMatchIndex((prev) => (matchCount > 0 ? Math.min(prev, matchCount - 1) : 0));
  }, [matchCount]);

  useEffect(() => {
    if (!showPlainToolbar || matchCount === 0) return;
    activeMatchRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentMatchIndex, matchCount, showPlainToolbar, activeTab, searchQuery]);

  useLayoutEffect(() => {
    if (!showPlainToolbar) {
      setToolbarHost(null);
      return;
    }
    const el = searchSlotRef.current;
    if (el) {
      setToolbarHost(el);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!cancelled) setToolbarHost(searchSlotRef.current);
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showPlainToolbar, searchSlotRef, activeTab]);

  const renderHighlighted = (
    text: string,
    ranges: { start: number; end: number; flatIndex: number }[]
  ): React.ReactNode => {
    if (!showPlainToolbar || ranges.length === 0) return text;
    const nodes: React.ReactNode[] = [];
    let pos = 0;
    ranges.forEach((range) => {
      if (range.end <= pos) return;
      const start = Math.max(range.start, pos);
      if (start > pos) nodes.push(text.slice(pos, start));
      const isActive = range.flatIndex === currentMatchIndex;
      nodes.push(
        <span
          key={`${range.flatIndex}-${range.start}`}
          ref={isActive ? (el) => { activeMatchRef.current = el; } : undefined}
          style={{
            backgroundColor: isActive ? '#ff9800' : '#ffeb3b',
            color: '#000',
            borderRadius: 1,
          }}
        >
          {text.slice(start, range.end)}
        </span>
      );
      pos = range.end;
    });
    if (pos < text.length) nodes.push(text.slice(pos));
    return nodes;
  };

  const renderHeaderCell = (text: string, row: number, field: 'key' | 'value') => {
    const ranges = headerSearch.byCell.get(`${row}:${field}`);
    if (!ranges) return text;
    return renderHighlighted(text, ranges);
  };

  const plainCopyLabel = activeTab === 4 ? headersCopyLabel : copyLabel;
  const plainCopyButton = (
    <Tooltip title={copied ? 'Copied!' : plainCopyLabel}>
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
        aria-label={plainCopyLabel}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const plainToolbarPortal =
    showPlainToolbar && toolbarHost
      ? createPortal(
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            caseInsensitive={caseInsensitive}
            onCaseInsensitiveChange={setCaseInsensitive}
            useRegex={useRegex}
            onUseRegexChange={setUseRegex}
            matchCount={matchCount}
            currentMatchIndex={currentMatchIndex}
            onNext={() => setCurrentMatchIndex((prev) => (prev + 1) % Math.max(1, matchCount))}
            onPrev={() => setCurrentMatchIndex((prev) => (prev - 1 + matchCount) % Math.max(1, matchCount))}
            compact
            overlay
            inOverlayGroup
            prefix={plainCopyButton}
          />,
          toolbarHost
        )
      : null;

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'white', overflow: 'hidden', position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          minWidth: 0,
          gap: 0,
        }}
      >
        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <SyncedTabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            TabIndicatorProps={LAZY_RESPONSE_BODY_TAB_INDICATOR}
            sx={lazyResponseBodyTabsSx}
          >
            <Tab label="Pretty" />
            <Tab label="Raw" />
            <Tab label="HTML" />
            <Tab label="XML" />
            <Tab label="Headers" />
          </SyncedTabs>
        </Box>
        <Box
          ref={searchSlotRef}
          sx={{
            flex: '1 1 260px',
            minWidth: 0,
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'stretch', md: 'flex-end' },
            px: 1,
            py: 0.5,
            boxSizing: 'border-box',
          }}
        />
        {plainToolbarPortal}
      </Box>
      <Box sx={{ maxHeight: height, overflowY: 'auto', minHeight: 0, position: 'relative' }}>
        {activeTab === 0 ? (
          <>
            {huge && !massive && !hugePretty && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>
                Formatting large response…
              </Typography>
            )}
            {virtualBody(massive ? (rawBody || displayRaw) : huge ? (hugePretty || displayPretty || displayRaw) : (displayPretty || displayRaw))}
          </>
        ) : activeTab === 1 ? (
          huge || massive ? (
            virtualBody(rawBody || '')
          ) : (
            <Box component="pre" sx={preSx}>
              {renderHighlighted(displayRaw, bodyMatches.map((range, index) => ({ ...range, flatIndex: index })))}
            </Box>
          )
        ) : activeTab === 2 ? (
          huge || massive ? (
            virtualBody(displayHtml || rawBody || '')
          ) : (
            <Box component="pre" sx={preSx}>
              {renderHighlighted(
                displayHtml || '(empty)',
                bodyMatches.map((range, index) => ({ ...range, flatIndex: index }))
              )}
            </Box>
          )
        ) : activeTab === 3 ? (
          huge || massive ? (
            virtualBody(displayXml || rawBody || '')
          ) : (
            <Box component="pre" sx={preSx}>
              {renderHighlighted(
                displayXml || '(empty)',
                bodyMatches.map((range, index) => ({ ...range, flatIndex: index }))
              )}
            </Box>
          )
        ) : activeTab === 4 ? (
          <Box sx={{ p: 2 }}>
            {headerEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {headersEmptyMessage}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid #e0e0e0' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600, width: '40%' }}>Key</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {headerEntries.map(([key, value], row) => (
                      <TableRow key={key} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13, verticalAlign: 'top' }}>{renderHeaderCell(key, row, 'key')}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderHeaderCell(value || '', row, 'value')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default LazyResponseBody;
